import React, { useEffect, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { ReactComponent as ListIcon } from "feather-icons/dist/icons/list.svg";
import { scrollToId } from "../utils/inline";

/** Sommaire de l'article, avec mise en évidence de la section visible. */

const Nav = tw.nav`rounded-lg bg-gray-100 p-6`;
const Title = styled.p`
  ${tw`flex items-center uppercase text-xs font-bold tracking-widest text-gray-900`}
  svg {
    ${tw`w-4 h-4 mr-2 text-primary-500`}
  }
`;
const List = tw.ol`mt-4`;
const Item = styled.li(({ $level, $active }) => [
  tw`border-l-2 border-gray-300 transition duration-300`,
  $level === 3 && tw`ml-4`,
  $active && tw`border-primary-500`,
]);
const ItemLink = styled.a(({ $active, $level }) => [
  tw`block pl-4 py-1 text-sm font-medium text-gray-600 hocus:text-primary-500 transition duration-300 leading-snug`,
  $level === 3 && tw`text-xs`,
  $active && tw`text-primary-500 font-bold`,
]);

const useActiveHeading = ids => {
  const [active, setActive] = useState(ids[0] || null);
  useEffect(() => {
    if (!ids.length || typeof IntersectionObserver === "undefined") return undefined;
    const elements = ids.map(id => document.getElementById(id)).filter(Boolean);
    if (!elements.length) return undefined;
    const visible = new Map();
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => visible.set(entry.target.id, entry.isIntersecting));
        const first = elements.find(el => visible.get(el.id));
        if (first) setActive(first.id);
      },
      { rootMargin: "-96px 0px -66% 0px", threshold: 0 }
    );
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [ids]);
  return active;
};

export default function TableOfContents({ headings, title = "Sommaire" }) {
  const ids = React.useMemo(() => (headings || []).map(h => h.id), [headings]);
  const active = useActiveHeading(ids);
  if (!headings || headings.length < 2) return null;
  return (
    <Nav aria-label={title}>
      <Title>
        <ListIcon aria-hidden="true" />
        {title}
      </Title>
      <List>
        {headings.map(h => (
          <Item key={h.id} $level={h.level} $active={active === h.id}>
            <ItemLink href={`#${h.id}`} $level={h.level} $active={active === h.id} aria-current={active === h.id ? "location" : undefined} onClick={e => scrollToId(h.id, e)}>
              {h.text}
            </ItemLink>
          </Item>
        ))}
      </List>
    </Nav>
  );
}
